/**
 * OnboardingWizard — 5-step onboarding experience for new users.
 *
 * Step 1: Welcome
 * Step 2: Profile setup (display_name, role, company, industry, jurisdiction)
 * Step 3: Feature interest selection
 * Step 4: Guided UI tour with spotlight overlay
 * Step 5: First-action CTAs + completion
 *
 * State is persisted to sessionStorage under `onboarding_state_{userId}`.
 * Redirects to /dashboard if onboarding is already completed or skipped.
 *
 * SAS-314: Onboarding flows for new users.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2 } from 'lucide-react';
import { ProgressStepper } from '../features/onboarding/ProgressStepper';
import { WelcomeStep } from '../features/onboarding/WelcomeStep';
import { ProfileSetupStep } from '../features/onboarding/ProfileSetupStep';
import { InterestSelectionStep } from '../features/onboarding/InterestSelectionStep';
import { GuidedTourStep } from '../features/onboarding/GuidedTourStep';
import { FirstActionStep } from '../features/onboarding/FirstActionStep';
import { onboardingApi } from '../api/onboarding';
import { useToast } from '../contexts/ToastContext';
import { useCurrentUserId } from '../contexts/UserContext';
import type {
  OnboardingStep,
  OnboardingState,
  ProfileFormData,
  ProfileFieldErrors,
  ApiError,
} from '../features/onboarding/types';

// ---------------------------------------------------------------------------
// Session storage helpers
// ---------------------------------------------------------------------------

function storageKey(userId: string) {
  return `onboarding_state_${userId}`;
}

function saveState(userId: string, state: OnboardingState) {
  try {
    sessionStorage.setItem(storageKey(userId), JSON.stringify(state));
  } catch {
    // Storage unavailable — non-fatal
  }
}

function loadState(userId: string): OnboardingState | null {
  try {
    const raw = sessionStorage.getItem(storageKey(userId));
    return raw ? (JSON.parse(raw) as OnboardingState) : null;
  } catch {
    return null;
  }
}

function clearState(userId: string) {
  try {
    sessionStorage.removeItem(storageKey(userId));
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Default wizard state
// ---------------------------------------------------------------------------

const DEFAULT_STATE: OnboardingState = {
  currentStep: 1,
  profileData: null,
  interests: [],
  tourProgress: 0,
};

// ---------------------------------------------------------------------------
// OnboardingWizardProps
// ---------------------------------------------------------------------------

interface OnboardingWizardProps {
  userId: string;
  orgId: string;
  /** Called on step 5 completion — parent should redirect to /dashboard or learning path. */
  onComplete: (recommendedPathId: string) => void;
  /** Called when user skips — parent should redirect to /dashboard. */
  onSkip: () => void;
}

// ---------------------------------------------------------------------------
// Main wizard component
// ---------------------------------------------------------------------------

function OnboardingWizardInner({ userId, orgId, onComplete, onSkip }: OnboardingWizardProps) {
  const toast = useToast();

  // Restore state from sessionStorage or start fresh
  const [state, setState] = useState<OnboardingState>(() => {
    const saved = loadState(userId);
    // If saved state exists but step is > 1 it means user refreshed mid-flow
    return saved ?? DEFAULT_STATE;
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profileFieldErrors, setProfileFieldErrors] = useState<ProfileFieldErrors>({});
  const [interestError, setInterestError] = useState<string | null>(null);
  const [focusFirstInterestCard, setFocusFirstInterestCard] = useState(false);

  // Ref to the profile form <form> element for programmatic submission
  const profileFormRef = useRef<HTMLFormElement>(null);

  const { currentStep, profileData, interests, tourProgress } = state;

  // Persist state to sessionStorage whenever it changes
  useEffect(() => {
    saveState(userId, state);
  }, [userId, state]);

  // ---------------------------------------------------------------------------
  // State helpers
  // ---------------------------------------------------------------------------

  function updateState(partial: Partial<OnboardingState>) {
    setState((prev) => ({ ...prev, ...partial }));
  }

  function advanceStep() {
    const next = Math.min(currentStep + 1, 5) as OnboardingStep;
    updateState({ currentStep: next });
  }

  function goBack() {
    const prev = Math.max(currentStep - 1, 1) as OnboardingStep;
    updateState({ currentStep: prev });
  }

  // ---------------------------------------------------------------------------
  // "Next" handler — step-specific logic
  // ---------------------------------------------------------------------------

  const handleNext = useCallback(async () => {
    if (currentStep === 1) {
      advanceStep();
      return;
    }

    if (currentStep === 2) {
      // Trigger form validation; the form's onValidSubmit will call handleProfileSubmit
      profileFormRef.current?.requestSubmit();
      return;
    }

    if (currentStep === 3) {
      if (interests.length === 0) {
        setInterestError('Please select at least one area of interest.');
        setFocusFirstInterestCard(true);
        // Reset focus trigger after one render cycle
        setTimeout(() => setFocusFirstInterestCard(false), 100);
        return;
      }
      await submitInterests();
      return;
    }

    if (currentStep === 4) {
      // Tour manages its own advancement; this is a fallback
      advanceStep();
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, interests]);

  // ---------------------------------------------------------------------------
  // Profile step submission
  // ---------------------------------------------------------------------------

  async function handleProfileSubmit(data: ProfileFormData) {
    setIsSubmitting(true);
    setProfileFieldErrors({});
    try {
      await onboardingApi.updateProfile(data);
      updateState({ profileData: data, currentStep: 3 });
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 400 && apiErr.details?.fields) {
        setProfileFieldErrors(apiErr.details.fields as ProfileFieldErrors);
      } else {
        toast.error(apiErr.message ?? 'Failed to save profile. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Interests step submission
  // ---------------------------------------------------------------------------

  async function submitInterests() {
    setIsSubmitting(true);
    setInterestError(null);
    try {
      await onboardingApi.updatePreferences(interests);
      // Only persist to sessionStorage after successful API call (AC12)
      advanceStep();
    } catch (err) {
      const apiErr = err as ApiError;
      // Don't persist to sessionStorage on failure (AC12)
      const msg = apiErr.message ?? 'Failed to save preferences.';
      setInterestError(msg);
    } finally {
      setIsSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Tour step handlers
  // ---------------------------------------------------------------------------

  function handleTourComplete() {
    updateState({ tourProgress: 4, currentStep: 5 });
  }

  function handleTourSkip() {
    updateState({ tourProgress: 4, currentStep: 5 });
  }

  // ---------------------------------------------------------------------------
  // Step 5: complete onboarding
  // ---------------------------------------------------------------------------

  async function handleComplete() {
    setIsSubmitting(true);
    try {
      const result = await onboardingApi.complete();
      clearState(userId);
      toast.success('Onboarding complete! You earned 50 points.');
      onComplete(result.recommended_path_id);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 409) {
        // Already completed from another tab
        clearState(userId);
        toast.info('You have already completed onboarding.');
        onComplete('');
        return;
      }
      toast.error(apiErr.message ?? 'Failed to complete onboarding.');
    } finally {
      setIsSubmitting(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Skip handler
  // ---------------------------------------------------------------------------

  async function handleSkip() {
    try {
      await onboardingApi.skip(currentStep);
    } catch {
      // Best-effort — don't block skip on API failure
    }
    clearState(userId);
    onSkip();
  }

  // ---------------------------------------------------------------------------
  // First action step handlers
  // ---------------------------------------------------------------------------

  function handleBrowseKB() {
    window.location.href = '/help/kb';
  }

  function handleCreateTicket() {
    window.location.href = '/help/tickets/new';
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const showBackButton = currentStep > 1 && currentStep < 5;
  const showSkipButton = currentStep < 5;
  const showNextButton = currentStep < 5;
  const isStep4 = currentStep === 4;

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-8">
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl ring-1 ring-gray-900/5 lg:max-w-3xl">

        {/* Progress stepper */}
        <ProgressStepper currentStep={currentStep} />

        {/* Step container */}
        <div className="min-h-[360px]">
          {currentStep === 1 && <WelcomeStep orgId={orgId} />}

          {currentStep === 2 && (
            <ProfileSetupStep
              formRef={profileFormRef}
              initialData={profileData}
              isSubmitting={isSubmitting}
              fieldErrors={profileFieldErrors}
              onValidSubmit={handleProfileSubmit}
            />
          )}

          {currentStep === 3 && (
            <InterestSelectionStep
              selected={interests}
              focusFirstCard={focusFirstInterestCard}
              error={interestError}
              onChange={(next) => {
                updateState({ interests: next });
                if (next.length > 0) setInterestError(null);
              }}
            />
          )}

          {currentStep === 4 && (
            <GuidedTourStep
              tourProgress={tourProgress}
              onTourComplete={handleTourComplete}
              onTourSkip={handleTourSkip}
              onProgressChange={(progress) => updateState({ tourProgress: progress })}
            />
          )}

          {currentStep === 5 && (
            <FirstActionStep
              isSubmitting={isSubmitting}
              onStartLearningPath={handleComplete}
              onBrowseKB={handleBrowseKB}
              onCreateTicket={handleCreateTicket}
            />
          )}
        </div>

        {/* Footer navigation — hidden on step 4 (tour manages its own nav) */}
        {!isStep4 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
            <div>
              {showBackButton && (
                <button
                  type="button"
                  onClick={goBack}
                  disabled={isSubmitting}
                  className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                  Back
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              {showSkipButton && (
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={isSubmitting}
                  className="rounded-md px-3 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Skip
                </button>
              )}

              {showNextButton && (
                <button
                  type={currentStep === 2 ? 'submit' : 'button'}
                  form={currentStep === 2 ? 'profile-form' : undefined}
                  onClick={currentStep !== 2 ? handleNext : undefined}
                  disabled={isSubmitting}
                  className="flex items-center gap-2 rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  )}
                  {currentStep === 1 ? 'Get Started' : 'Next'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Route-level wrapper — checks onboarding status, handles redirects
// ---------------------------------------------------------------------------

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const toast = useToast();
  const userId = useCurrentUserId();

  const [orgId, setOrgId] = useState<string | null>(null);
  const [statusChecked, setStatusChecked] = useState(false);

  // Poll onboarding status every 5s for concurrent-tab detection (AC8)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    onboardingApi
      .getStatus()
      .then((status) => {
        if (status.status === 'completed') {
          toast.info('You have already completed onboarding.');
          navigate('/dashboard', { replace: true });
          return;
        }
        if (status.status === 'skipped') {
          toast.info('You skipped onboarding. You can access setup from your profile.');
          navigate('/dashboard', { replace: true });
          return;
        }
        setOrgId(status.org_id);
        setStatusChecked(true);
      })
      .catch(() => {
        // If status check fails, allow the wizard to proceed with a fallback orgId
        setOrgId('your-organisation');
        setStatusChecked(true);
      });

    // Poll every 5s for concurrent-tab completion
    pollRef.current = setInterval(() => {
      onboardingApi.getStatus().then((status) => {
        if (status.status === 'completed') {
          clearInterval(pollRef.current!);
          toast.info('You have already completed onboarding.');
          navigate('/dashboard', { replace: true });
        }
      }).catch(() => undefined);
    }, 5000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!statusChecked || orgId === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm text-gray-500">Loading…</p>
        </div>
      </div>
    );
  }

  function handleComplete(recommendedPathId: string) {
    if (pollRef.current) clearInterval(pollRef.current);
    if (recommendedPathId) {
      navigate(`/learning/paths/${recommendedPathId}`, { replace: true });
    } else {
      navigate('/dashboard', { replace: true });
    }
  }

  function handleSkip() {
    if (pollRef.current) clearInterval(pollRef.current);
    navigate('/dashboard', { replace: true });
  }

  return (
    <OnboardingWizardInner
      userId={userId}
      orgId={orgId}
      onComplete={handleComplete}
      onSkip={handleSkip}
    />
  );
}
